import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';

import { type Bookmark, type BookmarkParams, createBookmark } from './bookmarkRibbon';

const TOOLTIP_DELAY_MS = 350;

// With reduced motion the ribbon still leaves and comes back, on the next frame.
const INSTANT: Partial<{ [K in keyof BookmarkParams]: Partial<BookmarkParams[K]> }> = {
  hover: { motion: { type: 'easing', duration: 0 } },
  remove: { tugDuration: 0, pull: { type: 'easing', duration: 0 }, tipLag: 0 },
  add: { feed: { type: 'easing', duration: 0 }, tipLead: 0, settle: 0 },
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface SavedSearchRibbonProps {
  /** The button's accessible name. */
  label: string;
  /** Shown under the ribbon after a short hover or on focus. */
  tooltip: string;
  /** True pulls the ribbon behind the card; false feeds it back out. */
  pulled: boolean;
  onClick: () => void;
  /** Runs once the ribbon is behind the card. */
  onPulled: () => void;
}

/**
 * The bookmark ribbon over a saved search card's top edge, as a button. The
 * card owns what removing means; this only moves the ribbon.
 */
export const SavedSearchRibbon = ({
  label,
  tooltip,
  pulled,
  onClick,
  onPulled,
}: SavedSearchRibbonProps) => {
  const hostRef = useRef<HTMLElement | null>(null);
  const bookmarkRef = useRef<Bookmark | null>(null);
  const tooltipTimerRef = useRef<number | undefined>(undefined);
  const onPulledRef = useRef(onPulled);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    onPulledRef.current = onPulled;
  });

  // The ribbon is plain DOM, so it mounts after hydration and leaves with the card.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const bookmark = createBookmark(host, prefersReducedMotion() ? { params: INSTANT } : {});
    bookmarkRef.current = bookmark;
    return () => {
      window.clearTimeout(tooltipTimerRef.current);
      bookmark.destroy();
      bookmarkRef.current = null;
    };
  }, []);

  // Each move starts once the one before it ends: a click right after Undo
  // pulls the ribbon as soon as it is back out. Only the latest request moves it.
  const motionRef = useRef<Promise<void>>(Promise.resolve());
  const requestRef = useRef(0);
  useEffect(() => {
    const bookmark = bookmarkRef.current;
    if (!bookmark) return;
    const request = (requestRef.current += 1);
    motionRef.current = motionRef.current.then(async () => {
      if (request !== requestRef.current) return;
      if (pulled) {
        if (await bookmark.remove()) onPulledRef.current();
      } else if (bookmark.getPhase() === 'removed') {
        await bookmark.add();
      }
    });
  }, [pulled]);

  const hideTooltip = useCallback(() => {
    window.clearTimeout(tooltipTimerRef.current);
    setShowTooltip(false);
  }, []);

  const handleGrab = useCallback(() => {
    if (pulled) return;
    bookmarkRef.current?.setHover(true);
    window.clearTimeout(tooltipTimerRef.current);
    tooltipTimerRef.current = window.setTimeout(() => setShowTooltip(true), TOOLTIP_DELAY_MS);
  }, [pulled]);

  const handleRelease = useCallback(() => {
    bookmarkRef.current?.setHover(false);
    hideTooltip();
  }, [hideTooltip]);

  const handleClick = useCallback(() => {
    hideTooltip();
    if (!pulled) onClick();
  }, [hideTooltip, onClick, pulled]);

  return (
    <Box
      ref={hostRef}
      // A click on a pulled ribbon does nothing, so it is not tracked either.
      analyticsName={pulled ? undefined : 'saved-search-remove'}
      aria-label={label}
      // A pulled ribbon keeps its spot, so a second click lands here and not on
      // the card under it, but it leaves the tab order until it is back.
      aria-disabled={pulled || undefined}
      tabIndex={pulled ? -1 : undefined}
      onClick={handleClick}
      onPointerEnter={handleGrab}
      onPointerLeave={handleRelease}
      onFocus={handleGrab}
      onBlur={handleRelease}
      // The artwork's top edge sits on the card's outer top edge, so the
      // ribbon reads as folded over it from behind.
      className={cn(
        'absolute top-[-2px] right-2.5 z-10 h-[38px] w-[29px] cursor-pointer appearance-none border-0 bg-transparent p-0',
        "after:absolute after:-inset-x-2 after:-top-1 after:-bottom-2.5 after:content-['']",
        'focus-visible:outline-none [&:focus-visible_svg]:rounded-[2px] [&:focus-visible_svg]:outline-2 [&:focus-visible_svg]:outline-offset-4 [&:focus-visible_svg]:outline-od-border-accent-brand [&:focus-visible_svg]:outline-solid',
        pulled && 'cursor-default',
      )}
    >
      <span
        aria-hidden
        className={cn(
          // Under the ribbon's tip and above the card, clear of the name row
          // and its controls.
          'pointer-events-none absolute top-[calc(100%+6px)] right-0 rounded-md bg-od-bg-inverse-primary px-2 py-1.5 text-body-xs leading-none whitespace-nowrap text-od-content-inverse-primary shadow-md transition-[opacity,transform] duration-150 ease-out',
          showTooltip ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
        )}
      >
        {tooltip}
      </span>
    </Box>
  );
};
